import React, { useState, useCallback } from 'react';
import { useCart } from '../CartContext';
import { useSession } from '../SessionContext';
import { ShoppingCart } from 'lucide-react';
import ProductDetail from './ProductDetail';
import './Cart.css';

function formatPrice(amount) {
  return `£${Number(amount).toFixed(2)}`;
}

const Cart = ({ products, onBack, onCheckout }) => {
  const { items, updateQuantity, updateItem, removeItem, total, itemCount } = useCart();
  const { member } = useSession();
  const [editingItem, setEditingItem] = useState(null);

  // Check if a product has modifiers or ingredients available for editing
  const hasEditableOptions = useCallback((item) => {
    const product = products?.find(p => p.id === item.product_id);
    if (!product) return false;
    const variation = item.variation_id
      ? (product.variations || []).find(v => v.id === item.variation_id)
      : null;
    const modifiers = variation?.modifiers || product.modifiers;
    const ingredients = product.ingredients;
    return (modifiers && Object.keys(modifiers).length > 0) || (ingredients && ingredients.length > 0);
  }, [products]);

  const handleEdit = useCallback((item) => {
    const product = products?.find(p => p.id === item.product_id);
    if (!product) return;
    const variation = item.variation_id
      ? (product.variations || []).find(v => v.id === item.variation_id)
      : null;
    setEditingItem({ cartItemId: item.id, product, variation });
  }, [products]);

  const handleEditComplete = useCallback((product, variation, quantity, modifiers, notes, modifierCost) => {
    if (!editingItem) return;
    const basePrice = Number(variation?.price || product.price);
    updateItem(editingItem.cartItemId, {
      modifiers,
      notes,
      quantity,
      price: basePrice + (modifierCost || 0)
    });
    setEditingItem(null);
  }, [editingItem, updateItem]);

  const discountPercent = member?.discount_percent || 0;
  const discountAmount = discountPercent > 0 ? total * (discountPercent / 100) : 0;
  const grandTotal = total - discountAmount;

  if (items.length === 0) {
    return (
      <div className="cart-screen">
        <div className="cart-header">
          <button className="cart-back-btn" onClick={onBack} aria-label="Back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h2 className="cart-title">Your Order</h2>
        </div>

        <div className="cart-empty">
          <div className="cart-empty-icon"><ShoppingCart size={48} strokeWidth={1.5} /></div>
          <p className="cart-empty-message">Your cart is empty</p>
          <p className="cart-empty-sub">Add some items to get started</p>
          <button className="cart-browse-btn" onClick={onBack}>Browse Menu</button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-screen">
      <div className="cart-header">
        <button className="cart-back-btn" onClick={onBack} aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="cart-title">Your Order</h2>
        <span className="cart-count-badge">{itemCount}</span>
      </div>

      <div className="cart-content">
        <ul className="cart-items-list">
          {items.map(item => {
            const modifierLabels = item.modifiers && Object.keys(item.modifiers).length > 0
              ? Object.values(item.modifiers).flat().join(', ')
              : null;

            const lineTotal = item.price * item.quantity;

            const editable = hasEditableOptions(item);

            return (
              <li
                key={item.id}
                className={`cart-item${editable ? ' cart-item--editable' : ''}`}
                onClick={editable ? () => handleEdit(item) : undefined}
                role={editable ? 'button' : undefined}
                tabIndex={editable ? 0 : undefined}
                onKeyDown={editable ? (e) => e.key === 'Enter' && handleEdit(item) : undefined}
              >
                <div className="cart-item-main">
                  <div className="cart-item-info">
                    <span className="cart-item-name">
                      {item.product_name}
                      {item.variation_name && (
                        <span className="cart-item-variation"> — {item.variation_name}</span>
                      )}
                    </span>
                    {modifierLabels && (
                      <span className="cart-item-modifiers">+ {modifierLabels}</span>
                    )}
                    {item.notes && (
                      <span className="cart-item-notes">{item.notes}</span>
                    )}
                  </div>

                  <div className="cart-item-price-remove">
                    <span className="cart-item-line-total">{formatPrice(lineTotal)}</span>
                    <button
                      className="cart-remove-btn"
                      onClick={(e) => { e.stopPropagation(); removeItem(item.id); }}
                      aria-label={`Remove ${item.product_name}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14H6L5 6" />
                        <path d="M10 11v6M14 11v6" />
                        <path d="M9 6V4h6v2" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="cart-item-qty-row" onClick={(e) => e.stopPropagation()}>
                  <div className="cart-qty-controls">
                    <button
                      className="cart-qty-btn"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <span className="cart-qty-value">{item.quantity}</span>
                    <button
                      className="cart-qty-btn"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                  <span className="cart-item-unit-price">{formatPrice(item.price)} each</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="cart-footer">
        <div className="cart-summary">
          <div className="cart-summary-row">
            <span>Subtotal</span>
            <span>{formatPrice(total)}</span>
          </div>

          {discountPercent > 0 && (
            <div className="cart-summary-row cart-summary-discount">
              <span>
                Member discount
                <span className="cart-discount-badge">−{discountPercent}%</span>
              </span>
              <span>−{formatPrice(discountAmount)}</span>
            </div>
          )}

          <div className="cart-summary-row cart-summary-total">
            <span>Total</span>
            <span>{formatPrice(grandTotal)}</span>
          </div>
        </div>

        <div className="cart-actions">
          <button className="cart-add-more-btn" onClick={onBack}>
            + Add More Items
          </button>
          <button
            className="cart-checkout-btn"
            onClick={onCheckout}
            disabled={itemCount === 0}
          >
            Go to Checkout
          </button>
        </div>
      </div>

      {editingItem && (
        <ProductDetail
          product={editingItem.product}
          preSelectedVariation={editingItem.variation}
          editMode
          editCartItem={items.find(i => i.id === editingItem.cartItemId)}
          onClose={() => setEditingItem(null)}
          onEditComplete={handleEditComplete}
        />
      )}
    </div>
  );
};

export default Cart;
